import { SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { useDebounce } from 'ahooks';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { ActivityLogEnum } from '../../../enums/activityLog';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';

type FormDataT = {
  searchQuery: string
};

const columns: any[] = [
  {
    field: 'dateTime',
    headerName: 'Date & time',
    width: 400,
    sortable: false,
    cellClassName: 'text-wrap',
    valueGetter: (params: any) =>
      `${DateTimeUtils.dateTimeFormat(new Date(params.row.createdAt))}`,
  },
  {
    field: 'activity',
    headerName: 'Activity type',
    width: 400,
    sortable: false,
    valueGetter: (params: any) => {
      const actionIndex = ActivityLogEnum.ActivityValueList.indexOf(params.row.action);
      if (actionIndex === -1) {
        return params.row.action;
      }
      return ActivityLogEnum.ActivityValueAndDisplayMappingList[actionIndex].label;
    },
  },
  {
    field: 'device',
    headerName: 'Device',
    width: 400,
    cellClassName: 'text-wrap',
    renderCell: (params: any) =>
      <span className="text-truncate">{params.row.deviceModel}</span>,
  },
];

const ActivityLogList: React.FC = () => {
  const { id: customerId } = useParams();
  const [activityLogList, setActivityLogList] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const useDebounced = useDebounce(searchQuery, { wait: 500 });

  const [pagination, setPagination] = React.useState({
    page: 0,
    pageSize: 10,
    rowCount: 0,
    loading: false,
  });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      searchQuery: '',
    },
  });

  const onSubmit = async (formData: FormDataT): Promise<void> => {
    setSearchQuery(formData.searchQuery);
  };

  const { service: getActivityLogListService } = useServices(ServiceNameEnum.customerMgmt.activityList);

  const getActivityLogList = async (): Promise<void> => {
    const pageOptions = { page: pagination.page + 1 };
    const queryString = {
      ...pageOptions,
      searchQuery,
    };
    const { data, error } = await getActivityLogListService.get({ params: { id: customerId }, queryString });
    if (data) {
      setActivityLogList(data.items);
      setPagination({
        page: data.pagination.currentPage - 1,
        pageSize: data.pagination.perPage,
        rowCount: data.pagination.total,
        loading: false,
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
  };

  const onPageChange = (page: number): void => {
    setPagination((prev) => ({
      ...prev,
      page,
    }));
  };

  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, loading: true }));
    getActivityLogList();
  }, [useDebounced, pagination.page, searchQuery]);

  return (
    <div style={{ padding: 24, borderRadius: '12px', boxShadow: '2px 2px 27px 0px rgba(0, 0, 0, 0.08)' }}>
      <h2 style={{ fontWeight: 'bold', margin: 0 }}>User activity log</h2>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        <span>{`Total ${pagination.rowCount} items`}</span>
        <form onSubmit={handleSubmit(onSubmit)} style={{ maxWidth: 500, width: '100%' }}>
          <Controller
            name="searchQuery"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                size="large"
                placeholder="Search by Activity"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  setSearchQuery(e.target.value);
                }}
                prefix={<SearchOutlined style={{ color: '#A1A1AA' }} />}
              />
            )}
          />
        </form>
      </div>
      <div style={{ marginTop: 24 }}>
        <AlumlyDataGrid
          rows={activityLogList}
          columns={columns}
          {...pagination}
          paginationMode="server"
          height={428}
          rowHeight={64}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
};

export default ActivityLogList;
